import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Upload, X, Star } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const ManageHairstyles = ({ onBack }) => {
  const [hairstyles, setHairstyles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Braiding',
    duration_minutes: 120,
    price_ngn: 0,
    deposit_required: null,
    size_options: [],
    length_options: [],
    with_extension: false,
    preparation_note: '',
    service_tag: 'basic',
    featured: false,
    is_published: false,
    images: [],
    cover_image: null,
    add_ons: [],
    variants: []
  });

  useEffect(() => {
    loadHairstyles();
  }, []);

  const loadHairstyles = async () => {
    try {
      const response = await api.getAllHairstyles();
      setHairstyles(response.data);
    } catch (error) {
      toast.error('Failed to load hairstyles');
    }
  };

  const handleImageUpload = async (files) => {
    setUploadingImages(true);
    try {
      const uploadedPaths = [];
      for (const file of files) {
        const response = await api.uploadFile(file);
        uploadedPaths.push(response.data.path);
      }
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedPaths],
        cover_image: prev.cover_image || uploadedPaths[0]
      }));
      toast.success(`${files.length} image(s) uploaded`);
    } catch (error) {
      toast.error('Image upload failed');
    } finally {
      setUploadingImages(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleImageUpload,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    multiple: true
  });

  const removeImage = (imagePath) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== imagePath),
      cover_image: prev.cover_image === imagePath ? (prev.images[0] || null) : prev.cover_image
    }));
  };

  const setCoverImage = (imagePath) => {
    setFormData(prev => ({ ...prev, cover_image: imagePath }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingId) {
        await api.updateHairstyle(editingId, formData);
        toast.success('Hairstyle updated successfully');
      } else {
        await api.createHairstyle(formData);
        toast.success('Hairstyle created successfully');
      }
      resetForm();
      loadHairstyles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save hairstyle');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (hairstyle) => {
    setFormData({
      name: hairstyle.name,
      description: hairstyle.description,
      category: hairstyle.category,
      duration_minutes: hairstyle.duration_minutes,
      price_ngn: hairstyle.price_ngn,
      deposit_required: hairstyle.deposit_required,
      size_options: hairstyle.size_options || [],
      length_options: hairstyle.length_options || [],
      with_extension: hairstyle.with_extension || false,
      preparation_note: hairstyle.preparation_note || '',
      service_tag: hairstyle.service_tag || 'basic',
      featured: hairstyle.featured || false,
      is_published: hairstyle.is_published || false,
      images: hairstyle.images || [],
      cover_image: hairstyle.cover_image || null,
      add_ons: hairstyle.add_ons || [],
      variants: hairstyle.variants || []
    });
    setEditingId(hairstyle.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hairstyle?')) return;
    
    try {
      await api.deleteHairstyle(id);
      toast.success('Hairstyle deleted');
      loadHairstyles();
    } catch (error) {
      toast.error('Failed to delete hairstyle');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'Braiding',
      duration_minutes: 120,
      price_ngn: 0,
      deposit_required: null,
      size_options: [],
      length_options: [],
      with_extension: false,
      preparation_note: '',
      service_tag: 'basic',
      featured: false,
      is_published: false,
      images: [],
      cover_image: null,
      add_ons: [],
      variants: []
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const token = localStorage.getItem('token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#3E2723]">
            {editingId ? 'Edit Hairstyle' : 'Add New Hairstyle'}
          </h2>
          <button
            onClick={resetForm}
            className="text-[#786E66] hover:text-[#3E2723]"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Hairstyle Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
                placeholder="e.g., Box Braids"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
                className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
              >
                <option value="Braiding">Braiding</option>
                <option value="Weaving">Weaving</option>
                <option value="Cornrows">Cornrows</option>
                <option value="Wig Services">Wig Services</option>
                <option value="Natural Hair">Natural Hair</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Price (NGN) *</label>
              <input
                type="number"
                value={formData.price_ngn}
                onChange={(e) => setFormData({ ...formData, price_ngn: parseFloat(e.target.value) })}
                required
                min="0"
                className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
                placeholder="25000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Deposit Required (NGN)</label>
              <input
                type="number"
                value={formData.deposit_required || ''}
                onChange={(e) => setFormData({ ...formData, deposit_required: e.target.value ? parseFloat(e.target.value) : null })}
                min="0"
                className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
                placeholder="10000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Duration (minutes) *</label>
              <input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                required
                min="0"
                className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
                placeholder="120"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Service Tag</label>
              <select
                value={formData.service_tag}
                onChange={(e) => setFormData({ ...formData, service_tag: e.target.value })}
                className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
              >
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={4}
              className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none resize-none"
              placeholder="Describe the hairstyle..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Preparation Note</label>
            <textarea
              value={formData.preparation_note}
              onChange={(e) => setFormData({ ...formData, preparation_note: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none resize-none"
              placeholder="e.g., Please come with washed hair"
            />
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-[#1A1A1A]">Images</label>
            
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-[#3E2723] bg-[#F3EBE1]' : 'border-[#E6DCD3] hover:border-[#3E2723]'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="h-12 w-12 text-[#786E66] mx-auto mb-4" />
              <p className="text-[#1A1A1A] mb-2">
                {uploadingImages ? 'Uploading...' : 'Drag & drop images here, or click to select'}
              </p>
              <p className="text-sm text-[#786E66]">PNG, JPG, JPEG, WEBP (max 5MB each)</p>
            </div>

            {formData.images.length > 0 && (
              <div className="grid grid-cols-4 gap-4">
                {formData.images.map((imgPath, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={getImageUrl(imgPath)}
                      alt={`Upload ${idx + 1}`}
                      className="w-full h-32 object-cover rounded-lg border-2 border-[#E6DCD3]"
                    />
                    {formData.cover_image === imgPath && (
                      <div className="absolute top-2 left-2 bg-[#C9A961] text-white px-2 py-1 rounded text-xs">
                        Cover
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {formData.cover_image !== imgPath && (
                        <button
                          type="button"
                          onClick={() => setCoverImage(imgPath)}
                          className="bg-white p-1 rounded shadow hover:bg-[#F3EBE1]"
                          title="Set as cover"
                        >
                          <Star className="h-4 w-4 text-[#3E2723]" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(imgPath)}
                        className="bg-white p-1 rounded shadow hover:bg-red-50"
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-[#1A1A1A]">Featured on homepage</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-[#1A1A1A]">Published (visible to public)</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.with_extension}
                onChange={(e) => setFormData({ ...formData, with_extension: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-[#1A1A1A]">Includes extension</span>
            </label>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={loading || uploadingImages}
              className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-lg px-8 py-3 font-medium transition-all disabled:opacity-50"
            >
              {loading ? 'Saving...' : editingId ? 'Update Hairstyle' : 'Create Hairstyle'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="border border-[#E6DCD3] text-[#3E2723] hover:bg-[#F3EBE1] rounded-lg px-8 py-3 font-medium transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-[#3E2723]">Manage Hairstyles</h2>
        <button
          onClick={() => setShowForm(true)}
          className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-lg px-6 py-3 inline-flex items-center gap-2 transition-all"
        >
          <Plus className="h-5 w-5" />
          Add New Hairstyle
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {hairstyles.map((style) => (
          <div key={style.id} className="bg-white rounded-lg border border-[#E6DCD3] overflow-hidden">
            {style.cover_image && (
              <img
                src={getImageUrl(style.cover_image)}
                alt={style.name}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-medium text-[#1A1A1A] mb-1">{style.name}</h3>
                  <p className="text-sm text-[#786E66]">{style.category}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(style)}
                    className="text-[#3E2723] hover:bg-[#F3EBE1] p-2 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(style.id)}
                    className="text-red-600 hover:bg-red-50 p-2 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-[#3E2723] font-medium mb-2">₦{style.price_ngn.toLocaleString()}</p>
              <div className="flex flex-wrap gap-2 text-xs">
                {style.featured && (
                  <span className="bg-[#C9A961] text-white px-2 py-1 rounded">Featured</span>
                )}
                <span className={`px-2 py-1 rounded ${style.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {style.is_published ? 'Published' : 'Draft'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {hairstyles.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-[#E6DCD3]">
          <p className="text-[#786E66] mb-4">No hairstyles created yet</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-lg px-6 py-3 inline-flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add Your First Hairstyle
          </button>
        </div>
      )}
    </div>
  );
};

export default ManageHairstyles;
